import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { assignmentService } from '../services/apiService'

const GradeAssignment = () => {
  const { id } = useParams()
  const [loading, setLoading] = useState(true)
  const [assignment, setAssignment] = useState(null)
  const [submissions, setSubmissions] = useState([])
  const [gradingSubmission, setGradingSubmission] = useState(null)
  const [gradeData, setGradeData] = useState({ score: '', feedback: '' })
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const fetchData = async () => {
    try {
      const [assignmentRes, submissionsRes] = await Promise.all([
        assignmentService.getAssignment(id),
        assignmentService.getAssignmentSubmissions(id),
      ])

      setAssignment(assignmentRes.data)
      const submissionsData = submissionsRes.data.results || submissionsRes.data
      setSubmissions(Array.isArray(submissionsData) ? submissionsData : [])
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleGradeClick = (submission) => {
    setGradingSubmission(submission)
    setGradeData({
      score: submission.score || '',
      feedback: submission.feedback || '',
    })
  }

  const handleGradeSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)

    try {
      await assignmentService.gradeSubmission(gradingSubmission.id, gradeData)
      alert('Submission graded successfully!')
      setGradingSubmission(null)
      fetchData() // Refresh data
    } catch (error) {
      console.error('Error grading submission:', error)
      alert('Failed to grade submission')
    } finally {
      setSubmitting(false)
    }
  }

  const getStats = () => {
    const total = submissions.length
    const graded = submissions.filter((s) => s.is_graded).length
    const pending = total - graded
    const avgScore =
      graded > 0
        ? (
            submissions
              .filter((s) => s.is_graded)
              .reduce((sum, s) => sum + s.score, 0) / graded
          ).toFixed(1)
        : 0

    return { total, graded, pending, avgScore }
  }

  const stats =
    submissions.length > 0
      ? getStats()
      : { total: 0, graded: 0, pending: 0, avgScore: 0 }

  if (loading) {
    return (
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <p className='text-gray-600'>Loading...</p>
      </div>
    )
  }

  if (!assignment) {
    return (
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <p className='text-red-600'>Assignment not found</p>
      </div>
    )
  }

  return (
    <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
      {/* Header */}
      <div className='mb-8'>
        <Link
          to={`/courses/${assignment.course}/manage`}
          className='text-primary-600 hover:text-primary-700 mb-4 inline-block'
        >
          ← Back to Manage Course
        </Link>
        <h1 className='text-3xl font-bold text-gray-900'>{assignment.title}</h1>
        <p className='mt-2 text-gray-600'>Grade Student Submissions</p>
      </div>

      {/* Stats */}
      <div className='grid grid-cols-1 md:grid-cols-4 gap-6 mb-8'>
        <div className='bg-white rounded-lg shadow p-6'>
          <div className='text-sm font-medium text-gray-600'>
            Total Submissions
          </div>
          <div className='mt-2 text-3xl font-bold text-primary-600'>
            {stats.total}
          </div>
        </div>
        <div className='bg-white rounded-lg shadow p-6'>
          <div className='text-sm font-medium text-gray-600'>Graded</div>
          <div className='mt-2 text-3xl font-bold text-green-600'>
            {stats.graded}
          </div>
        </div>
        <div className='bg-white rounded-lg shadow p-6'>
          <div className='text-sm font-medium text-gray-600'>Pending</div>
          <div className='mt-2 text-3xl font-bold text-orange-600'>
            {stats.pending}
          </div>
        </div>
        <div className='bg-white rounded-lg shadow p-6'>
          <div className='text-sm font-medium text-gray-600'>Average Score</div>
          <div className='mt-2 text-3xl font-bold text-blue-600'>
            {stats.avgScore}
          </div>
        </div>
      </div>

      {/* Submissions List */}
      <div className='bg-white rounded-lg shadow overflow-hidden'>
        <div className='px-6 py-4 border-b border-gray-200'>
          <h2 className='text-xl font-semibold text-gray-900'>Submissions</h2>
        </div>

        {submissions.length === 0 ? (
          <div className='px-6 py-12 text-center'>
            <p className='text-gray-500'>No submissions yet</p>
          </div>
        ) : (
          <div className='divide-y divide-gray-200'>
            {submissions.map((submission) => (
              <div
                key={submission.id}
                className='px-6 py-4 hover:bg-gray-50 transition'
              >
                <div className='flex items-start justify-between'>
                  <div className='flex-1'>
                    <h3 className='text-lg font-medium text-gray-900'>
                      {submission.student_name}
                    </h3>
                    <p className='text-sm text-gray-600 mt-1'>
                      Submitted:{' '}
                      {new Date(submission.submitted_at).toLocaleString()}
                    </p>

                    {submission.is_late && (
                      <span className='inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800 mt-2'>
                        Late Submission
                      </span>
                    )}

                    {/* Content */}
                    <div className='mt-3 bg-gray-50 rounded p-3'>
                      <p className='text-sm text-gray-700 whitespace-pre-wrap'>
                        {submission.content}
                      </p>
                      {submission.file && (
                        <a
                          href={submission.file}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='text-primary-600 hover:text-primary-700 text-sm mt-2 inline-block'
                        >
                          📎 View Attached File
                        </a>
                      )}
                    </div>

                    {/* Grade Info */}
                    {submission.is_graded && (
                      <div className='mt-3 bg-green-50 border border-green-200 rounded p-3'>
                        <div className='flex items-center justify-between'>
                          <span className='text-sm font-medium text-green-800'>
                            Score: {submission.score}/{assignment.max_score}
                          </span>
                        </div>
                        {submission.feedback && (
                          <p className='text-sm text-green-700 mt-2'>
                            Feedback: {submission.feedback}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className='ml-6'>
                    <button
                      onClick={() => handleGradeClick(submission)}
                      className={`px-4 py-2 rounded-md text-sm font-medium ${
                        submission.is_graded
                          ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          : 'bg-primary-600 text-white hover:bg-primary-700'
                      }`}
                    >
                      {submission.is_graded ? 'Edit Grade' : 'Grade'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Grading Modal */}
      {gradingSubmission && (
        <div className='fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50'>
          <div className='relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white'>
            <div className='flex justify-between items-center mb-4'>
              <h3 className='text-lg font-medium text-gray-900'>
                Grade Submission - {gradingSubmission.student_name}
              </h3>
              <button
                onClick={() => setGradingSubmission(null)}
                className='text-gray-400 hover:text-gray-600'
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleGradeSubmit}>
              <div className='mb-4'>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Score (out of {assignment.max_score})
                </label>
                <input
                  type='number'
                  min='0'
                  max={assignment.max_score}
                  value={gradeData.score}
                  onChange={(e) =>
                    setGradeData({ ...gradeData, score: e.target.value })
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500'
                  required
                />
              </div>

              <div className='mb-4'>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Feedback
                </label>
                <textarea
                  value={gradeData.feedback}
                  onChange={(e) =>
                    setGradeData({ ...gradeData, feedback: e.target.value })
                  }
                  rows='4'
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500'
                  placeholder='Provide feedback to the student...'
                />
              </div>

              <div className='flex justify-end space-x-3'>
                <button
                  type='button'
                  onClick={() => setGradingSubmission(null)}
                  className='px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={submitting}
                  className='px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50'
                >
                  {submitting ? 'Saving...' : 'Save Grade'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default GradeAssignment
